import type {
	Scalars,
	shipments,
	shipments_routes,
	shipments_routes_bool_exp,
	shipments_routesRequest,
	shipments_routes_steps_shipments
} from '$lib/tools/graphdb';

import { shipments_state } from '$lib/features/routes/states/shipments_state';
import { balance_state } from '$lib/features/profile/states/balance_state';
import { driver_state } from '$lib/features/profile/states/driver_state';
import { create_store } from '$lib/tools/graph-db/stores/create_store.svelte';
import { graphql } from '$lib/tools/graphdb';
import { utils } from '$lib/utils/utils.svelte';
import { resolve_current_route, apply_returned_step_shipment } from './routes_logic';

export type Route = shipments_routes;
export type RouteStep = Route['Steps'][number];
export type RouteStepType = RouteStep['type'];
type ShipmentStatuses = Scalars['shipment_status'];

export type ShipmentNote = {
	role: 'driver' | 'seller' | 'operator' | 'buyer' | 'undefined_buyer';
	user_id: string;
	note: string;
	timestamp: Date;
};

/**
 * Return fields for every shipments_routes read (query, subscription).
 * Typed against the generated `shipments_routesRequest` so the nested Steps
 * array tuple (`Steps: [{ order_by }, { fields }]`) keeps its exact shape.
 */
const ROUTE_FIELDS: shipments_routesRequest = {
	id: true,
	is_active: true,
	date_created: true,
	date_updated: true,
	route_number: true,
	route_shape_string: true,
	computed_start_time: true,
	computed_total_collection_amount: true,
	computed_driver_cost: true,
	assigned_driver_id: true,
	Steps: [
		{ order_by: [{ step_order: 'asc' }] },
		{
			id: true,
			status: true,
			distance_m: true,
			driver_cost: true,
			duration_s: true,
			type: true,
			step_order: true,
			eta: true,
			Address: {
				id: true,
				description: true,
				location: true,
				city: true,
				name: true
			},
			Shipments: {
				id: true,
				shipments_routes_step_id: true,
				shipment_id: true,
				type: true,
				is_completed: true
			}
		}
	]
};

const routes_store = create_store<shipments_routes>({
	name: 'routes',
	keys: { main: 'date_created', last_updated: 'date_updated' },
	fetch: {
		many: (opts) =>
			graphql.query_many(
				'shipments_routes',
				opts?.where ? { where: opts.where as shipments_routes_bool_exp } : undefined,
				ROUTE_FIELDS
			) as Promise<shipments_routes[] | null>,
		one: (id) => graphql.query_one('shipments_routes_by_pk', { id }, ROUTE_FIELDS) as Promise<shipments_routes | null>
	},
	stream: (max) => (on_batch) =>
		graphql.subscribe(
			'shipments_routes_stream',
			{ batch_size: 10, cursor: [{ initial_value: { date_updated: max } }] },
			ROUTE_FIELDS,
			(rows) => on_batch(rows),
			(error) => console.error('shipments_routes_stream error', error)
		),
	auto: { populate: true, subscribe: true, prune: true }
});

// All-routes reactive view (RAM ∝ visible rows; liveQuery over the Dexie table).
const routes_view = routes_store.where({ index: 'date_created', sort: 'asc' });

// Finished routes are filtered at access time: `is_active` is a boolean, which
// Dexie cannot index (IndexableType excludes booleans), so an index-backed
// `equals: false` view is impossible — filter the all-routes view instead.
const finished_routes_view = routes_view;

// Writer set for `assigned_route_id` (last write wins): RoutesSwitcherModal.svelte (switcher),
// pickup/deliver auto-claim (below), `back_to_active` (DriveScreen.svelte — banner callback,
// not on-mount), finish-clear (below), resolution self-heal (Routes.svelte + foreground
// re-validation in initiate_main_states). Do NOT add mount-time writes.
const assigned_handlers = {
	assigned_route_id: utils.states.create_persisted_string_async({
		initial_value: '',
		key: 'assigned_route_id',
		init_fn: get_assigned_route_id
	}),

	pickup_shipment: async (options: {
		shipment_id: string;
		step_shipment_id: string;
		route_id: string;
	}): Promise<boolean> => {
		try {
			const { shipment_id, step_shipment_id, route_id } = options;

			if (!shipment_id) throw new Error('invalid_arguments');

			const update_op_result = (await graphql.mutate(
				'request_shipment_status_change',
				{
					shipment_id,
					return_fields: shipments_state.utils.return_fields,
					payload: {
						requested_status: 'picked_up',
						step_shipment_id
					}
				},
				{ data: true, error: true }
			)) as { data?: unknown; error?: unknown } | null;

			const update_op = update_op_result?.data as {
				shipment: shipments;
				step_shipment: shipments_routes_steps_shipments;
			};
			if (!update_op?.shipment?.id) throw new Error('op failed');

			assigned_handlers.assigned_route_id.value = route_id;
			balance_state.fetch_balance();

			// Merge the returned server state into the store: refetch the route
			// (authoritative — includes the returned step_shipment) and the shipment.
			await Promise.all([
				route_id ? routes_store.sync.refetch(route_id) : Promise.resolve(),
				shipments_state.fetch_one(update_op.shipment.id)
			]);

			return true;
		} catch (error) {
			console.error({ error });
			return false;
		}
	},

	deliver_shipment: async (options: {
		shipment: shipments;
		step_shipment_id: string;
		collected_amount: number;
		note?: string;
		route_id: string;
	}): Promise<ShipmentStatuses | null> => {
		try {
			const { shipment, step_shipment_id, collected_amount, note, route_id } = options;

			if (shipment.status !== 'picked_up' || typeof collected_amount !== 'number' || !isFinite(collected_amount))
				throw new Error('shipment_not_picked_up_or_collected_amount_not_number');

			const update_op = (await graphql.mutate(
				'request_shipment_status_change',
				{
					shipment_id: shipment.id,
					return_fields: shipments_state.utils.return_fields,
					payload: {
						requested_status: 'delivered',
						step_shipment_id,
						amount_paid: collected_amount,
						reason: note ?? null
					}
				},
				{ data: true, error: true }
			)) as { data?: unknown; error?: unknown } | null;

			const update_op_result = update_op?.data as {
				shipment: shipments;
				step_shipment: shipments_routes_steps_shipments;
			};
			if (!update_op_result?.shipment?.id) throw new Error('op failed');

			balance_state.fetch_balance();
			assigned_handlers.assigned_route_id.value = route_id;

			await Promise.all([
				route_id ? routes_store.sync.refetch(route_id) : Promise.resolve(),
				shipments_state.fetch_one(update_op_result.shipment.id)
			]);

			return 'delivered' as ShipmentStatuses;
		} catch (error) {
			console.error({ error });
			return null;
		}
	},

	finish_shipping_route: async (route_id: string): Promise<boolean> => {
		try {
			if (!route_id) throw new Error('route_id');

			const mutate_op = (await graphql.mutate('finish_scheduled_route', { route_id }, { data: true, error: true })) as {
				data?: unknown;
				error?: unknown;
			} | null;

			if (!mutate_op || !mutate_op.data) throw new Error('mutate_op_failed');

			await Promise.all([routes_store.sync.refresh(), balance_state.fetch_balance()]);
			assigned_handlers.assigned_route_id.value = '';

			return true;
		} catch (error) {
			console.error(error);
			return false;
		}
	}
};

async function get_assigned_route_id(stored_value: string | null): Promise<string | null> {
	try {
		const active_routes = (await routes_store.materialize()).filter((route) => route.is_active);
		return resolve_current_route(active_routes, stored_value)?.id ?? null;
	} catch (error) {
		console.error({ error });
		return null;
	}
}

export const routes_state = {
	...routes_store,

	get value(): Route[] {
		return routes_view.values;
	},

	get values(): Route[] {
		return routes_view.values;
	},

	/** Refetch a single route from the server and persist it locally. */
	fetch_one: (id: string): Promise<Route | null> =>
		routes_store.sync.refetch(id).then(() => routes_store.get(id).value ?? null),

	assigned: assigned_handlers,

	get_finished_routes: {
		get value(): Route[] {
			return finished_routes_view.values.filter(
				(route) => !route.is_active && route.assigned_driver_id === driver_state.value?.id
			);
		}
	}
};

export { resolve_current_route, apply_returned_step_shipment };
